import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { BarChart3, Clock, Coins, LayoutGrid, Sparkles } from 'lucide-react';

import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { EmptyState, KpiCard, PillTabs, QuotaBar, RelativeTime, SectionCard, formatNumber } from '../../../components/design-system/admin';
import { BarList } from '../../../components/design-system/analytics/BarList';
import { TrendChart } from '../../../components/design-system/analytics/TrendChart';
import { useFeatureUsageApi } from '../../../common/hooks/use-feature-usage-api.hook';
import type { UsageDays, VendorUsageDetail } from '../../../common/models/feature-usage.model';
import { FEATURE_META, formatMetric, formatSpend, formatUsd, headline, spellOut } from './usage-meta';
import { vendorPageKey } from './vendor-pages';

type Series = 'aiCostUsd' | 'aiCredits' | 'logins' | 'pageViews';
const DAYS: UsageDays[] = [7, 30, 90, 365];

/** `agent:assistant` → "Agent · assistant": an AI feature's slug, readable. */
const aiFeatureName = (slug: string) => slug.split(':').map((part) => spellOut(part)).join(' · ');

/**
 * One vendor's use of the backoffice (its Usage tab): every feature with its
 * numbers and when it was last used, AI by feature in dollars and in credits,
 * what its credits went on, its activity per day, its plan quotas, who signs
 * in and which pages they open.
 */
export function VendorUsagePanel({ vendorId }: { vendorId: string }) {
  const { t } = useTranslation();
  const api = useFeatureUsageApi();
  const apiRef = useRef(api);
  apiRef.current = api;
  const [days, setDays] = useState<UsageDays>(30);
  const [series, setSeries] = useState<Series>('aiCostUsd');
  const [data, setData] = useState<VendorUsageDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setFailed(false);
    try {
      setData(await apiRef.current.vendor(vendorId, days));
    } catch {
      setFailed(true);
    } finally {
      setLoading(false);
    }
  }, [vendorId, days]);

  useEffect(() => {
    load();
  }, [load]);

  if (failed && !data) {
    return (
      <Card>
        <EmptyState
          icon={<BarChart3 />}
          title={t('ADMIN_SHELL.COMMON.LOAD_FAILED')}
          action={
            <Button variant="outline" size="sm" onClick={load}>
              {t('ADMIN_SHELL.COMMON.RETRY')}
            </Button>
          }
        />
      </Card>
    );
  }

  const m = data?.metrics || {};
  const featureName = (k: string) => t(`ADMIN_USAGE.FEATURES.${k}`);
  const metricName = (k: string) => t(`ADMIN_USAGE.METRICS.${k}`);
  const quotaName = (k: string) => t(`ADMIN_USAGE.QUOTA.${k}`, { defaultValue: spellOut(k.replace(/^max/, '')) });
  const seriesFormat = (v: number) => (series === 'aiCostUsd' ? formatUsd(v) : formatNumber(v));

  return (
    <div className={cn('flex flex-col gap-4', loading && data && 'opacity-70 transition-opacity')} data-testid="vendor-usage">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-muted-foreground">{t('ADMIN_USAGE.VENDOR.INTRO')}</p>
        <PillTabs
          value={days}
          onChange={setDays}
          ariaLabel={t('ADMIN_USAGE.PERIOD.LABEL')}
          options={DAYS.map((d) => ({ value: d, label: t(`ADMIN_USAGE.PERIOD.D${d}`) }))}
        />
      </div>

      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <KpiCard
          label={t('ADMIN_USAGE.VENDOR.LAST_SEEN')}
          value={data?.lastSeenAt ? <RelativeTime value={data.lastSeenAt} /> : t('ADMIN_USAGE.NEVER')}
          hint={t('ADMIN_USAGE.VENDOR.USERS', { count: m.activeUsers || 0 })}
          icon={<Clock />}
          loading={!data}
        />
        <KpiCard
          label={t('ADMIN_USAGE.KPI.AI_COST')}
          value={formatUsd(m.aiCostUsd)}
          hint={t('ADMIN_USAGE.KPI.AI_COST_HINT', { credits: formatNumber(m.aiCredits || 0), tokens: formatMetric('aiTokens', m.aiTokens) })}
          icon={<Sparkles />}
          loading={!data}
          testId="vendor-usage-ai"
        />
        <KpiCard
          label={t('ADMIN_USAGE.KPI.CREDITS')}
          value={formatNumber(m.creditsUsed || 0)}
          hint={
            data?.creditBalance !== null && data?.creditBalance !== undefined
              ? t('ADMIN_USAGE.VENDOR.BALANCE', { count: data.creditBalance, usd: formatUsd((m.creditsUsed || 0) * (data?.usdPerCredit || 0.01)) })
              : undefined
          }
          icon={<Coins />}
          loading={!data}
          testId="vendor-usage-credits"
        />
        <KpiCard
          label={t('ADMIN_USAGE.VENDOR.FEATURES')}
          value={data ? `${data.used.length}/${FEATURE_META.length}` : '—'}
          hint={t('ADMIN_USAGE.VENDOR.FEATURES_HINT')}
          icon={<LayoutGrid />}
          loading={!data}
        />
      </div>

      {/* Each feature, in the same order as the overview's strip. */}
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3" data-testid="vendor-usage-features">
        {FEATURE_META.map((f) => {
          const used = !!data?.used.includes(f.key);
          const Icon = f.icon;
          const lastAt = data?.last?.[f.key];
          return (
            <Card key={f.key} className={cn('gap-3 px-4 py-4', data && !used && 'bg-muted/30')} data-testid={`vendor-feature-${f.key}`} data-used={used}>
              <div className="flex items-center justify-between gap-2">
                <span className="flex items-center gap-2 text-sm">
                  <span className={cn('flex size-6 items-center justify-center rounded-md bg-muted', used ? 'text-foreground' : 'text-muted-foreground')}>
                    <Icon className="size-3.5" />
                  </span>
                  {featureName(f.key)}
                </span>
                {data && (
                  <span className={cn('text-xs', used ? 'text-foreground' : 'text-muted-foreground')}>
                    {used ? t('ADMIN_USAGE.USED') : t('ADMIN_USAGE.NOT_USED')}
                  </span>
                )}
              </div>
              {!data ? (
                <Skeleton className="h-12 w-full" />
              ) : (
                <>
                  <div className="flex items-baseline gap-2">
                    <span className={cn('font-heading text-2xl tabular-nums', !used && 'text-muted-foreground')}>
                      {formatMetric(f.metrics[0], headline(f.key, m))}
                    </span>
                    <span className="text-xs text-muted-foreground">{metricName(f.metrics[0])}</span>
                  </div>
                  <dl className="grid gap-1 text-xs">
                    {f.metrics.slice(1).map((k) => (
                      <div key={k} className="flex justify-between gap-2">
                        <dt className="text-muted-foreground">{metricName(k)}</dt>
                        <dd className="tabular-nums">{formatMetric(k, m[k])}</dd>
                      </div>
                    ))}
                    {f.key === 'ads' && formatSpend(data.spend) && (
                      <div className="flex justify-between gap-2">
                        <dt className="text-muted-foreground">{t('ADMIN_USAGE.METRICS.adSpend')}</dt>
                        <dd className="tabular-nums">{formatSpend(data.spend)}</dd>
                      </div>
                    )}
                    {f.key === 'credits' && data.creditBalance !== null && (
                      <div className="flex justify-between gap-2">
                        <dt className="text-muted-foreground">{t('ADMIN_USAGE.METRICS.creditBalance')}</dt>
                        <dd className="tabular-nums">{formatNumber(data.creditBalance)}</dd>
                      </div>
                    )}
                  </dl>
                  <p className="text-xs text-muted-foreground">
                    {lastAt ? (
                      <>
                        {t('ADMIN_USAGE.VENDOR.LAST_USED')} <RelativeTime value={lastAt} />
                      </>
                    ) : (
                      t('ADMIN_USAGE.VENDOR.NOT_IN_PERIOD')
                    )}
                  </p>
                </>
              )}
            </Card>
          );
        })}
      </div>

      <SectionCard
        title={t('ADMIN_USAGE.VENDOR.ACTIVITY')}
        description={t('ADMIN_USAGE.VENDOR.ACTIVITY_HINT')}
        action={
          <PillTabs
            value={series}
            onChange={setSeries}
            ariaLabel={t('ADMIN_USAGE.VENDOR.ACTIVITY')}
            options={[
              { value: 'aiCostUsd' as Series, label: t('ADMIN_USAGE.VENDOR.SERIES_USD') },
              { value: 'aiCredits' as Series, label: t('ADMIN_USAGE.VENDOR.SERIES_CREDITS') },
              { value: 'logins' as Series, label: t('ADMIN_USAGE.METRICS.logins') },
              { value: 'pageViews' as Series, label: t('ADMIN_USAGE.METRICS.pageViews') },
            ]}
          />
        }
      >
        {!data ? (
          <Skeleton className="h-[200px] w-full" />
        ) : (
          <TrendChart
            data={data.daily.map((d) => ({ label: d.day, value: d[series] }))}
            valueLabel={series === 'aiCostUsd' ? t('ADMIN_USAGE.VENDOR.SERIES_USD') : series === 'aiCredits' ? t('ADMIN_USAGE.VENDOR.SERIES_CREDITS') : t(`ADMIN_USAGE.METRICS.${series}`)}
            height={200}
            partialLast
            partialLabel={t('ADMIN_USAGE.SO_FAR')}
            formatLabel={(d) => new Date(`${d}T00:00:00`).toLocaleDateString(undefined, { day: 'numeric', month: 'short' })}
            formatValue={seriesFormat}
            testId="vendor-usage-trend"
          />
        )}
      </SectionCard>

      <div className="grid gap-4 xl:grid-cols-2">
        <SectionCard title={t('ADMIN_USAGE.VENDOR.AI_TITLE')} description={t('ADMIN_USAGE.VENDOR.AI_HINT')} flush testId="vendor-usage-ai-table">
          {!data ? (
            <div className="px-6 pb-6">
              <Skeleton className="h-24 w-full" />
            </div>
          ) : !data.ai.length ? (
            <EmptyState icon={<Sparkles />} title={t('ADMIN_USAGE.VENDOR.NO_AI')} />
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="ps-6">{t('ADMIN_USAGE.VENDOR.AI_FEATURE')}</TableHead>
                  <TableHead className="text-end">{t('ADMIN_USAGE.METRICS.aiTokens')}</TableHead>
                  <TableHead className="text-end">{t('ADMIN_USAGE.VENDOR.USD')}</TableHead>
                  <TableHead className="pe-6 text-end">{t('ADMIN_USAGE.VENDOR.CREDITS')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.ai.map((r) => (
                  <TableRow key={r.feature}>
                    <TableCell className="ps-6">
                      <div>{aiFeatureName(r.feature)}</div>
                      <div className="text-xs text-muted-foreground">
                        {t('ADMIN_USAGE.VENDOR.REQUESTS', { count: r.requests })}
                        {r.errors > 0 && ` · ${t('ADMIN_USAGE.VENDOR.ERRORS', { count: r.errors })}`}
                      </div>
                    </TableCell>
                    <TableCell className="text-end tabular-nums">{formatMetric('aiTokens', r.tokens)}</TableCell>
                    <TableCell className="text-end tabular-nums">{formatUsd(r.costUsd)}</TableCell>
                    <TableCell className="pe-6 text-end tabular-nums">{formatNumber(r.credits)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </SectionCard>

        <SectionCard title={t('ADMIN_USAGE.VENDOR.CREDITS_TITLE')} description={t('ADMIN_USAGE.VENDOR.CREDITS_HINT')} testId="vendor-usage-credit-reasons">
          {!data ? (
            <Skeleton className="h-24 w-full" />
          ) : (
            <BarList
              items={data.credits.map((c) => ({
                key: c.reason,
                label: c.reason,
                value: c.credits,
                secondary: t('ADMIN_USAGE.VENDOR.CHARGES', { count: c.entries }),
              }))}
              format={(v) => formatNumber(v)}
              emptyText={t('ADMIN_USAGE.VENDOR.NO_CREDITS')}
            />
          )}
        </SectionCard>
      </div>

      <div className="grid gap-4 xl:grid-cols-3">
        <SectionCard title={t('ADMIN_USAGE.VENDOR.QUOTA_TITLE')} description={t('ADMIN_USAGE.VENDOR.QUOTA_HINT', { plan: data?.vendor.plan || '' })} testId="vendor-usage-quota">
          {!data ? (
            <Skeleton className="h-24 w-full" />
          ) : data.quotaUnavailable ? (
            <p className="text-sm text-muted-foreground" data-testid="vendor-usage-quota-unavailable">{t('ADMIN_USAGE.VENDOR.QUOTA_UNAVAILABLE')}</p>
          ) : !data.quota.length ? (
            <p className="text-sm text-muted-foreground">{t('ADMIN_USAGE.VENDOR.NO_QUOTA')}</p>
          ) : (
            <ul className="grid gap-3">
              {data.quota.map((q) => (
                <li key={q.key} className="grid gap-1">
                  <div className="flex justify-between gap-2 text-xs">
                    <span>{quotaName(q.key)}</span>
                    <span className="text-muted-foreground tabular-nums">
                      {formatNumber(q.current)}
                      {q.limit >= 0 ? ` / ${formatNumber(q.limit)}` : ''}
                    </span>
                  </div>
                  <QuotaBar used={q.current} limit={q.limit} />
                </li>
              ))}
            </ul>
          )}
        </SectionCard>

        <SectionCard title={t('ADMIN_USAGE.VENDOR.PEOPLE_TITLE')} description={t('ADMIN_USAGE.VENDOR.PEOPLE_HINT')} testId="vendor-usage-people">
          {!data ? (
            <Skeleton className="h-24 w-full" />
          ) : !data.people.length ? (
            <p className="text-sm text-muted-foreground">{t('ADMIN_USAGE.VENDOR.NO_PEOPLE')}</p>
          ) : (
            <ul className="grid gap-2">
              {data.people.map((p, i) => (
                <li key={p.email || i} className="flex items-center justify-between gap-3 text-sm">
                  <span className="min-w-0">
                    <span className="block truncate" dir="ltr">
                      {p.email || '—'}
                    </span>
                    <span className="block truncate text-xs text-muted-foreground">{t('ADMIN_USAGE.VENDOR.LOGINS', { count: p.logins })}</span>
                  </span>
                  <RelativeTime value={p.lastSeenAt} className="shrink-0 text-xs text-muted-foreground" />
                </li>
              ))}
            </ul>
          )}
        </SectionCard>

        <SectionCard title={t('ADMIN_USAGE.VENDOR.PAGES_TITLE')} description={t('ADMIN_USAGE.VENDOR.PAGES_HINT')} testId="vendor-usage-pages">
          {!data ? (
            <Skeleton className="h-24 w-full" />
          ) : (
            <BarList
              items={data.pages.slice(0, 12).map((p) => {
                const key = vendorPageKey(p.page);
                return { key: p.page, label: key ? t(`ADMIN_USAGE.PAGES.${key}`) : <span dir="ltr">{p.page}</span>, value: p.views };
              })}
              format={(v) => formatNumber(v)}
              emptyText={t('ADMIN_USAGE.VENDOR.NO_PAGES')}
            />
          )}
        </SectionCard>
      </div>
    </div>
  );
}
